'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import styles from './SkipLink.module.css';

/**
 * "Skip to main content" (WCAG 2.4.1). Rendered ahead of the cookie banner so
 * it is the first Tab stop. Targets the page's own <main> landmark, so it only
 * shows on pages that render one; pages are not wrapped in a shared <main>
 * because many already render their own.
 */
// The page's landmark, not a <main> inside an open dialog.
const findMain = (): HTMLElement | null =>
  Array.from(document.querySelectorAll('main')).find(
    (el) => !el.closest('[aria-modal="true"]'),
  ) ?? null;

export function SkipLink() {
  const t = useTranslations('common');
  const pathname = usePathname();
  const [hasTarget, setHasTarget] = useState(false);

  useEffect(() => {
    if (findMain()) {
      setHasTarget(true);
      return;
    }
    setHasTarget(false);
    // A <main> rendered behind Suspense or a dynamic import mounts after this
    // effect; watch briefly for it instead of missing it on that page.
    const observer = new MutationObserver(() => {
      if (!findMain()) return;
      setHasTarget(true);
      observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeout = window.setTimeout(() => observer.disconnect(), 10000);
    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [pathname]);

  if (!hasTarget) return null;

  return (
    <a
      href="#main-content"
      className={styles.skipLink}
      onClick={(e) => {
        const target = findMain();
        if (!target) return;
        e.preventDefault();
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
          // Focusable only so the skip lands here; no ring around the page.
          target.style.outline = 'none';
        }
        target.focus();
        target.scrollIntoView();
      }}
    >
      {t('skipLink.label')}
    </a>
  );
}
